import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Hammer, LogOut, Megaphone, Receipt, ShoppingCart } from "lucide-react";
import { useMgmtBrand } from "@/lib/useMgmtBrand";
import { useStayPut } from "@/lib/useStayPut";
import LangToggle from "@/components/LangToggle";
import { t as T } from "@/lib/i18n";
import InstallAppButton from "@/components/InstallAppButton";
import PushToggle from "@/components/PushToggle";
import { api } from "@/lib/api";
import { useHeaderHeightVar } from "@/lib/useHeaderHeightVar";

/**
 * Worker portal layout. Header always shows Sign Out (was missing —
 * that was the primary UX complaint that motivated this build).
 * Report issue / Expenses / Shopping list live here as icon buttons —
 * WorkerDashboard hands their open-handlers up via Outlet context so the
 * page body doesn't need a whole extra button row on phone/tablet.
 */
export default function WorkerLayout() {
  useMgmtBrand("Management USA NJ — Worker");
  useStayPut();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const headerRef = useHeaderHeightVar();
  const [headerActions, setHeaderActions] = useState({});
  // Radix dialog portals live on <body>, so we tag body itself with the
  // emerald theme so worker-side dialogs (Report issue, Add expense, …)
  // pick it up too.
  useEffect(() => {
    document.body.classList.add("theme-emerald", "worker-portal-shell");
    return () => {
      document.body.classList.remove("theme-emerald", "worker-portal-shell");
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/managementusa", { replace: true });
  };

  return (
    <div className="min-h-screen bg-emerald-50/40 theme-emerald">
      <header ref={headerRef} className="worker-pwa-header sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur" data-testid="worker-header">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <Hammer className="w-5 h-5 text-emerald-700 self-center" />
            <span className="text-lg font-black tracking-tighter text-emerald-800 font-display">WORKER</span>
            <span className="text-xs font-light tracking-wide text-muted-foreground hidden sm:inline">PORTAL</span>
          </div>
          <div className="flex min-w-0 items-center gap-1 sm:gap-1.5 flex-wrap justify-end">
            <LangToggle />
            <PushToggle client={api} base="/push" targetUrl="/worker" testidPrefix="worker-push" />
            <InstallAppButton manifest="/manifest-worker.json" appleIcon="/mgmt-apple-touch.png" appName="the Management USA NJ worker portal" iosTitle="Worker" className="h-9" />
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{T("Signed in as")}</div>
              <div className="max-w-[12rem] text-sm font-medium [overflow-wrap:anywhere]" data-testid="worker-me-label">{headerActions.workerName || user?.name || user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm shrink-0 px-2 sm:px-3"
              onClick={handleLogout}
              title={T("Sign out")}
              data-testid="worker-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> <span>{T("Sign out")}</span>
            </Button>
          </div>
        </div>
        {(headerActions.onReportIssue || headerActions.onExpenses || headerActions.onShoppingList) && (
          <div className="max-w-6xl mx-auto px-4 pb-2 flex flex-wrap gap-1 [&>button]:max-w-full">
            {headerActions.onReportIssue && (
              <button
                type="button"
                onClick={headerActions.onReportIssue}
                className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition text-muted-foreground hover:bg-secondary hover:text-foreground shrink-0"
                data-testid="worker-nav-feedback"
              >
                <Megaphone className="w-4 h-4" /> {T("Report issue")}
              </button>
            )}
            {headerActions.onExpenses && (
              <button
                type="button"
                onClick={headerActions.onExpenses}
                className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition text-muted-foreground hover:bg-secondary hover:text-foreground shrink-0"
                data-testid="worker-nav-expenses"
              >
                <Receipt className="w-4 h-4" /> {T("Expenses")}
              </button>
            )}
            {headerActions.onShoppingList && (
              <button
                type="button"
                onClick={headerActions.onShoppingList}
                className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition text-muted-foreground hover:bg-secondary hover:text-foreground shrink-0"
                data-testid="worker-nav-shopping"
              >
                <ShoppingCart className="w-4 h-4" /> {T("Shopping list")}
              </button>
            )}
          </div>
        )}
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6" data-testid="worker-main">
        <Outlet context={{ setHeaderActions }} />
      </main>
    </div>
  );
}
